import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TextField, Typography } from '@mui/material';

type Props = {
  value: any;
  label?: string;
  required?: boolean;
  helperText?: string;
  onChange: (value: any, error: string | null) => void;
  classes?: {
    root?: string;
    input?: string;
    label?: string;
    helperText?: string;
  };
};

export const JsonTextAreaField: React.FC<Props> = ({
  value,
  label,
  required,
  helperText = 'Enter a JSON object: { "someKey": "Hello", ... }',
  onChange,
  classes = {
    root: 'mb-4 text-mainText',
    label: 'text-medium',
    input: 'bg-gray-50 text-small',
    helperText: 'text-small italic',
  },
}) => {
  const [input, setInput] = useState<string>(
    JSON.stringify(value ?? {}, null, 2)
  );
  const [error, setError] = useState<string | null>(null);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (!isInternalChange.current) {
      setInput(JSON.stringify(value ?? {}, null, 2));
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const raw = e.target.value;
      setInput(raw);
      isInternalChange.current = true;

      try {
        const parsed = JSON.parse(raw);
        setError(null);
        onChange(parsed, null);
      } catch {
        const message = 'Invalid JSON';
        setError(message);
        onChange(undefined, message);
      }
    },
    [onChange]
  );

  return (
    <div className={classes.root}>
      <TextField
        className={classes.input}
        label={`${label} ${required ? '*' : ''}`}
        multiline
        fullWidth
        minRows={6}
        value={input}
        onChange={handleInputChange}
        error={!!error}
        helperText={<span className={classes.helperText}>{error || ' '}</span>}
        variant="outlined"
        slotProps={{
          input: {
            style: { fontFamily: 'monospace' },
          },
        }}
      />
      <Typography
        variant="caption"
        className={classes?.helperText}
        sx={{
          color: error ? 'error.main' : 'text.secondary',
        }}
      >
        {!error && helperText}
      </Typography>
    </div>
  );
};
